import { useState } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import AgentSelector from './AgentSelector';
import { XMarkIcon } from '@heroicons/react/24/outline';

export default function DemoAssignmentModal({ demo, onClose, onSuccess, teamLeaderCode = null }) {
  const [selectedAgentId, setSelectedAgentId] = useState(demo?.payload?.agentCode || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const currentAgent = demo?.payload?.agentCode || 'Unassigned';
  const referrer = demo?.payload?.referrer || null;

  const handleAssign = async () => {
    if (!selectedAgentId) {
      setError('Please select an agent');
      return;
    }

    if (selectedAgentId === currentAgent) {
      setError('This agent is already assigned to this demo');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const demoRef = doc(db, 'demo_bookings', demo.id);
      
      // Update only the agentCode, PRESERVE the referrer field
      await updateDoc(demoRef, {
        'payload.agentCode': selectedAgentId,
        'updated_at': new Date()
      });

      onSuccess?.();
      onClose();
    } catch (err) {
      console.error('Error assigning demo:', err);
      setError('Failed to assign demo. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-lg font-semibold text-gray-900">Assign Demo to Agent</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-4 space-y-4">
          {/* Demo Info */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Demo Details</h3>
            <div className="space-y-1 text-sm">
              <p><span className="font-medium">Customer:</span> {demo?.payload?.customer?.name || 'N/A'}</p>
              <p><span className="font-medium">Date:</span> {demo?.payload?.dateTime?.demoDate || 'N/A'}</p>
              <p><span className="font-medium">Time:</span> {demo?.payload?.dateTime?.timeSlot || 'N/A'}</p>
              <p><span className="font-medium">Location:</span> {demo?.payload?.location || 'N/A'}</p>
              {referrer && (
                <p className="text-indigo-600">
                  <span className="font-medium">Referrer:</span> {referrer} (will be preserved)
                </p>
              )}
            </div>
          </div>

          {/* Current Agent */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Current Agent
            </label>
            <div className="px-3 py-2 bg-gray-100 rounded-md text-sm text-gray-900">
              {currentAgent}
            </div>
          </div>

          {/* Agent Selector */}
          <AgentSelector
            selectedAgentId={selectedAgentId}
            onSelect={setSelectedAgentId}
            excludeReferrers={true}
            teamLeaderCode={teamLeaderCode}
          />

          {/* Important Note */}
          {referrer && (
            <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
              <p className="text-sm text-blue-800">
                <strong>Note:</strong> The referrer field will remain unchanged. Only the assigned agent will be updated.
              </p>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            onClick={handleAssign}
            disabled={loading || !selectedAgentId}
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {loading ? 'Assigning...' : 'Assign Agent'}
          </button>
        </div>
      </div>
    </div>
  );
}
